import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal", "backdrop", "grid", "loading", "empty", "selected", "hiddenInput",
    "search", "loadMore", "previewModal", "previewImage", "previewVideo",
    "previewSource", "previewCaption"
  ]
  static values = {
    url: String,
    mediaType: { type: String, default: "photos" }, // "photos" or "videos"
    variantIndex: Number,
    maxItems: { type: Number, default: 9 },
    limitReached: { type: String, default: "Maximum %{count} items allowed" },
    remainingSlots: { type: String, default: "You can only select %{count} more item(s)" },
    previewLabel: { type: String, default: "Preview" },
    libraryLabel: { type: String, default: "Library" }
  }

  connect() {
    this.selectedBlobs = new Set()
    this.mediaData = []
    this.page = 1
    this.totalPages = 1
    this.searchTimeout = null
    this.requestId = 0
    this.handleEscape = this.handleEscape.bind(this)
  }

  disconnect() {
    clearTimeout(this.searchTimeout)
    document.removeEventListener("keydown", this.handleEscape)
    document.body.classList.remove("overflow-hidden")
  }

  open(event) {
    event?.preventDefault()

    // Get current count of attached media
    const currentCount = this.getCurrentAttachedCount()
    const remainingSlots = this.maxItemsValue - currentCount

    if (remainingSlots <= 0) {
      alert(this.limitReachedValue.replace("%{count}", this.maxItemsValue))
      return
    }

    this.selectedBlobs.clear()
    this.updateSelectedCount()
    this.page = 1
    this.totalPages = 1
    this.mediaData = []
    if (this.hasSearchTarget) this.searchTarget.value = ""

    document.body.classList.add("overflow-hidden")
    document.addEventListener("keydown", this.handleEscape)

    // Show backdrop first
    this.backdropTarget.classList.remove("hidden")
    // Slide in the panel
    this.modalTarget.classList.remove("translate-x-full")

    // Fade in backdrop after a frame
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.backdropTarget.classList.remove("opacity-0")
      })
    })

    this.loadMedia({ reset: true })
  }

  close() {
    document.body.classList.remove("overflow-hidden")
    document.removeEventListener("keydown", this.handleEscape)

    // Slide out panel and fade backdrop
    this.modalTarget.classList.add("translate-x-full")
    this.backdropTarget.classList.add("opacity-0")

    setTimeout(() => {
      this.backdropTarget.classList.add("hidden")
    }, 300)
    this.closePreview()
  }

  handleEscape(event) {
    if (event.key !== "Escape") return

    if (this.hasPreviewModalTarget && !this.previewModalTarget.classList.contains("hidden")) {
      this.closePreview(event)
    } else {
      this.close()
    }
  }

  search() {
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => {
      this.page = 1
      this.totalPages = 1
      this.mediaData = []
      this.loadMedia({ reset: true })
    }, 250)
  }

  loadMore() {
    if (this.page >= this.totalPages) return

    this.page += 1
    this.loadMedia({ append: true })
  }

  async loadMedia({ reset = false, append = false } = {}) {
    const requestId = ++this.requestId

    if (!append) this.showLoading()
    if (append && this.hasLoadMoreTarget) {
      this.loadMoreTarget.disabled = true
      this.loadMoreTarget.classList.add("opacity-60")
    }

    try {
      const mediaTypeFilter = this.mediaTypeValue === "photos" ? "photos" : "videos"
      const params = new URLSearchParams({
        media_type: mediaTypeFilter,
        per_page: "60",
        page: String(this.page)
      })

      if (this.hasSearchTarget && this.searchTarget.value.trim()) {
        params.set("q", this.searchTarget.value.trim())
      }

      const response = await fetch(`${this.urlValue}.json?${params.toString()}`, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) {
        console.error("Media picker fetch failed:", response.status, response.statusText)
        throw new Error(`Failed to load media: ${response.status}`)
      }

      const data = await response.json()
      if (requestId !== this.requestId) return

      this.totalPages = data.pagination?.pages || 1
      this.mediaData = reset ? (data.items || []) : [...this.mediaData, ...(data.items || [])]
      this.renderGrid()
    } catch (error) {
      console.error("Error loading media:", error)
      this.showEmpty()
    } finally {
      if (this.hasLoadMoreTarget) {
        this.loadMoreTarget.disabled = false
        this.loadMoreTarget.classList.remove("opacity-60")
      }
    }
  }

  showLoading() {
    this.loadingTarget.classList.remove("hidden")
    this.gridTarget.classList.add("hidden")
    this.emptyTarget.classList.add("hidden")
    if (this.hasLoadMoreTarget) this.loadMoreTarget.classList.add("hidden")
  }

  showEmpty() {
    this.loadingTarget.classList.add("hidden")
    this.gridTarget.classList.add("hidden")
    this.emptyTarget.classList.remove("hidden")
    if (this.hasLoadMoreTarget) this.loadMoreTarget.classList.add("hidden")
  }

  renderGrid() {
    this.loadingTarget.classList.add("hidden")

    // Get already attached blob IDs to exclude from selection
    const attachedBlobIds = this.getAttachedBlobIds()
    const availableItems = this.mediaData.filter(item => !attachedBlobIds.includes(item.blob_id))

    if (availableItems.length === 0) {
      if (this.page < this.totalPages) {
        this.loadMore()
        return
      }

      this.showEmpty()
      return
    }

    this.gridTarget.classList.remove("hidden")
    this.emptyTarget.classList.add("hidden")
    this.gridTarget.innerHTML = availableItems.map(item => this.renderMediaItem(item)).join("")
    this.updateLoadMore()
  }

  updateLoadMore() {
    if (!this.hasLoadMoreTarget) return

    this.loadMoreTarget.classList.toggle("hidden", this.page >= this.totalPages)
  }

  renderMediaItem(item) {
    const isPhoto = item.media_type === "photo"
    const isSelected = this.selectedBlobs.has(item.blob_id)
    const title = this.escapeHtml(item.filename || "")
    const subtitle = this.escapeHtml([item.product_name, item.variant_name].filter(Boolean).join(" / ") || "Unknown")

    return `
      <div class="relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${isSelected ? 'border-teal-500 ring-2 ring-teal-500' : 'border-transparent hover:border-slate-300'}"
           data-action="click->media-picker#toggleSelect"
           data-picker-blob-id="${item.blob_id}">
        <div class="aspect-square bg-slate-100 dark:bg-slate-700">
          ${isPhoto && item.thumbnail_url
            ? `<img src="${this.escapeAttribute(item.thumbnail_url)}" class="w-full h-full object-cover" loading="lazy" decoding="async" alt="${title}">`
            : `<div class="w-full h-full flex items-center justify-center">
                <svg class="w-10 h-10 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z"/>
                </svg>
               </div>`
          }
        </div>
        <div class="absolute inset-0 transition-opacity opacity-0 group-hover:opacity-100" style="background-color: rgba(0,0,0,0.2)"></div>
        <button type="button"
                class="absolute top-2 left-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-slate-700 opacity-0 shadow transition-opacity hover:bg-white group-hover:opacity-100 dark:bg-slate-900/90 dark:text-slate-200"
                data-action="click->media-picker#preview"
                data-picker-blob-id="${item.blob_id}"
                title="${this.previewLabelValue}">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M2.25 12s3.75-6.75 9.75-6.75S21.75 12 21.75 12 18 18.75 12 18.75 2.25 12 2.25 12z"/>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
        </button>
        ${isSelected ? `
          <div class="absolute top-2 right-2 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
          </div>
        ` : ''}
        <div class="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
          <p class="text-xs text-white truncate">${subtitle}</p>
          <p class="text-[10px] text-white/80 truncate">${title}</p>
        </div>
      </div>
    `
  }

  preview(event) {
    event.preventDefault()
    event.stopPropagation()

    const blobId = parseInt(event.currentTarget.dataset.pickerBlobId)
    const item = this.mediaData.find(media => media.blob_id === blobId)
    if (!item || !item.preview_url) return

    this.previewCaptionTarget.textContent = item.filename || ""
    this.previewModalTarget.classList.remove("hidden")

    if (item.media_type === "photo") {
      this.previewImageTarget.src = item.preview_url
      this.previewImageTarget.alt = item.filename || ""
      this.previewImageTarget.classList.remove("hidden")
      this.previewVideoTarget.classList.add("hidden")
    } else {
      this.previewSourceTarget.src = item.preview_url
      this.previewSourceTarget.type = item.content_type || "video/mp4"
      this.previewVideoTarget.load()
      this.previewVideoTarget.classList.remove("hidden")
      this.previewImageTarget.classList.add("hidden")
    }
  }

  closePreview(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (!this.hasPreviewModalTarget) return

    this.previewModalTarget.classList.add("hidden")
    this.previewImageTarget.removeAttribute("src")
    this.previewVideoTarget.pause()
    this.previewSourceTarget.removeAttribute("src")
    this.previewVideoTarget.load()
  }

  handlePreviewBackdrop(event) {
    if (event.target === this.previewModalTarget) {
      this.closePreview(event)
    }
  }

  toggleSelect(event) {
    const item = event.currentTarget
    const blobId = parseInt(item.dataset.pickerBlobId)

    const currentCount = this.getCurrentAttachedCount()
    const remainingSlots = this.maxItemsValue - currentCount

    if (this.selectedBlobs.has(blobId)) {
      this.selectedBlobs.delete(blobId)
      // Remove visual selection
      item.classList.remove('border-teal-500', 'ring-2', 'ring-teal-500')
      item.classList.add('border-transparent')
      const checkmark = item.querySelector('.bg-teal-500.rounded-full')
      if (checkmark) checkmark.remove()
    } else {
      if (this.selectedBlobs.size >= remainingSlots) {
        alert(this.remainingSlotsValue.replace("%{count}", remainingSlots))
        return
      }
      this.selectedBlobs.add(blobId)
      // Add visual selection
      item.classList.remove('border-transparent')
      item.classList.add('border-teal-500', 'ring-2', 'ring-teal-500')
      // Add checkmark
      const checkmark = document.createElement('div')
      checkmark.className = 'absolute top-2 right-2 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center'
      checkmark.innerHTML = '<svg class="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>'
      item.appendChild(checkmark)
    }

    this.updateSelectedCount()
  }

  updateSelectedCount() {
    if (this.hasSelectedTarget) {
      this.selectedTarget.textContent = this.selectedBlobs.size
    }
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;")
  }

  escapeAttribute(value) {
    return this.escapeHtml(value)
  }

  getCurrentAttachedCount() {
    // Count existing photos/videos in the variant form
    const container = this.element.closest('.variant-fields')
    if (!container) return 0

    const existingItems = container.querySelectorAll(`[data-sortable-photos-type-value="${this.mediaTypeValue}"] [data-attachment-item]`)
    return existingItems.length
  }

  getAttachedBlobIds() {
    // Get blob IDs of already attached media
    const container = this.element.closest('.variant-fields')
    if (!container) return []

    const items = container.querySelectorAll(`[data-sortable-photos-type-value="${this.mediaTypeValue}"] [data-blob-id]`)
    return Array.from(items).map(el => parseInt(el.dataset.blobId))
  }

  confirm() {
    if (this.selectedBlobs.size === 0) {
      this.close()
      return
    }

    // Add selected blob IDs to hidden input
    const blobIds = Array.from(this.selectedBlobs)

    if (this.hasHiddenInputTarget) {
      const existingValue = this.hiddenInputTarget.value
      let existingIds = []
      try {
        existingIds = existingValue ? JSON.parse(existingValue) : []
      } catch (e) {
        existingIds = []
      }
      this.hiddenInputTarget.value = JSON.stringify([...existingIds, ...blobIds])
    }

    // Add preview thumbnails to the form
    this.addPreviewThumbnails(blobIds)

    // Dispatch event so other controllers can react
    this.dispatch("selected", { detail: { blobIds, mediaType: this.mediaTypeValue } })

    this.close()
  }

  addPreviewThumbnails(blobIds) {
    // Find the preview section within this controller's element
    const previewSection = this.element.querySelector('[data-photo-preview-target="preview"]')
    const previewGrid = this.element.querySelector('[data-photo-preview-target="previewGrid"]')

    if (!previewSection || !previewGrid) return

    // Get selected items data
    const selectedItems = this.mediaData.filter(item => blobIds.includes(item.blob_id))

    selectedItems.forEach(item => {
      const div = document.createElement('div')
      div.className = 'relative aspect-square rounded-md overflow-hidden bg-slate-100 dark:bg-slate-700 ring-2 ring-teal-500'
      div.dataset.pendingBlobId = item.blob_id

      if (item.media_type === 'photo' && item.thumbnail_url) {
        div.innerHTML = `
          <img src="${item.thumbnail_url}" class="w-full h-full object-cover" alt="${item.filename}">
          <div class="absolute top-1 left-1 bg-teal-500 text-white text-xs font-medium rounded px-1.5 py-0.5">${this.libraryLabelValue}</div>
        `
      } else {
        div.innerHTML = `
          <div class="w-full h-full flex flex-col items-center justify-center p-2">
            <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z"/>
            </svg>
            <span class="text-xs text-slate-500 truncate w-full text-center mt-1">${item.filename}</span>
          </div>
          <div class="absolute top-1 left-1 bg-teal-500 text-white text-xs font-medium rounded px-1.5 py-0.5">${this.libraryLabelValue}</div>
        `
      }

      previewGrid.appendChild(div)
    })

    // Show the preview section
    previewSection.classList.remove('hidden')
  }
}
